import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Plus, Trash2, Upload, ArrowLeft, Save } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["everyday", "premium", "exotic", "medicinal"];
const UOMS = ["100g", "200g", "250g", "500g", "1kg"];
const BADGES = ["", "Bestseller", "Popular", "Premium", "Gourmet", "Trending", "Brain Food", "Rare", "New"];

const emptyRecipe  = () => ({ name: "", description: "", time: "30 mins", difficulty: "Medium", image: "", ingredients: [""], steps: [""] });
const emptyBenefit = () => ({ title: "", desc: "" });

export default function AdminMushroomForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authHeader } = useAuth();
  const isEdit = Boolean(id);

  const [form, setForm] = useState({
    name: "", scientificName: "", price: "", uom: "250g", qty: "50",
    badge: "", category: "everyday", description: "", longDescription: "",
    image: "", cookingTip: "", benefits: [emptyBenefit()], uses: [""],
    recipes: [emptyRecipe()], isUpcoming: false, preOrderFrom: "", preOrderUntil: "",
    isActive: true
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("basic");

  useEffect(() => {
    if (!isEdit) return;
    axios.get(`${API}/admin/mushrooms/${id}`, authHeader()).then(r => {
      const d = r.data;
      setForm({
        ...d,
        price: String(d.price), qty: String(d.qty),
        preOrderFrom: d.preOrderFrom || "", preOrderUntil: d.preOrderUntil || "",
        isActive: d.isActive !== false,
        benefits: d.benefits?.length ? d.benefits : [emptyBenefit()],
        uses: d.uses?.length ? d.uses : [""],
        recipes: d.recipes?.length ? d.recipes.map(r => ({
          ...r,
          ingredients: r.ingredients?.length ? r.ingredients : [""],
          steps: r.steps?.length ? r.steps : [""]
        })) : [emptyRecipe()]
      });
    }).catch(() => navigate("/admin/mushrooms"));
  }, [id]);

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, data, authHeader());
      set("image", `${BACKEND}${res.data.url}`);
    } catch { setError("Image upload failed"); }
    finally { setUploading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        ...form, price: parseInt(form.price), qty: parseInt(form.qty),
        preOrderFrom: form.preOrderFrom || null, preOrderUntil: form.preOrderUntil || null,
        uses: form.uses.filter(Boolean), benefits: form.benefits.filter(b => b.title),
        recipes: form.recipes.map(r => ({
          ...r, ingredients: r.ingredients.filter(Boolean), steps: r.steps.filter(Boolean)
        }))
      };
      if (isEdit) {
        await axios.put(`${API}/admin/mushrooms/${id}`, payload, authHeader());
      } else {
        await axios.post(`${API}/admin/mushrooms`, payload, authHeader());
      }
      navigate("/admin/mushrooms");
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const TABS = [
    { id: "basic", label: "Basic Info" },
    { id: "details", label: "Details" },
    { id: "recipes", label: "Recipes" },
    { id: "preorder", label: "Pre-Order" },
  ];

  const inputCls = "w-full px-3 py-2.5 rounded-xl border border-[#273A2F] text-[#F2F4F3] text-sm placeholder-[#A8BCAE] focus:outline-none focus:border-[#D97A46]/50 transition-colors bg-[#0F1612]";
  const labelCls = "block text-xs font-medium text-[#A8BCAE] mb-1.5";

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-3xl" data-testid="mushroom-form">
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => navigate("/admin/mushrooms")} className="p-2 rounded-xl text-[#A8BCAE] hover:text-[#F2F4F3] border border-[#273A2F] hover:border-[#D97A46]/30 transition-all">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="font-serif text-2xl sm:text-3xl font-bold text-[#F2F4F3]">{isEdit ? "Edit Mushroom" : "Add New Mushroom"}</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-xl border border-[#273A2F]" style={{ backgroundColor: '#0F1612' }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all ${tab === t.id ? "text-white" : "text-[#A8BCAE] hover:text-[#F2F4F3]"}`}
            style={tab === t.id ? { backgroundColor: '#D97A46' } : {}}>
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info Tab */}
        {tab === "basic" && (
          <div className="space-y-5 p-6 rounded-2xl border border-[#273A2F]" style={{ backgroundColor: '#16201A' }}>
            {/* Image Upload */}
            <div>
              <label className={labelCls}>Product Image</label>
              <div className="flex items-start gap-4">
                <div className="w-24 h-24 rounded-xl overflow-hidden border border-[#273A2F] flex-shrink-0" style={{ backgroundColor: '#0F1612' }}>
                  {form.image ? <img src={form.image} alt="preview" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#A8BCAE] text-xs">No image</div>}
                </div>
                <div className="flex-1">
                  <label data-testid="image-upload-label" className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#273A2F] text-sm text-[#A8BCAE] hover:text-[#F2F4F3] hover:border-[#D97A46]/30 cursor-pointer transition-all w-fit">
                    <Upload className="w-4 h-4" />
                    {uploading ? "Uploading..." : "Upload from Gallery"}
                    <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} data-testid="image-file-input" />
                  </label>
                  <p className="text-xs text-[#A8BCAE] mt-2">Or paste a URL below</p>
                  <input className={`${inputCls} mt-1.5`} placeholder="https://..." value={form.image} onChange={e => set("image", e.target.value)} data-testid="image-url-input" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className={labelCls}>Mushroom Name *</label>
                <input className={inputCls} required value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Oyster Mushroom" data-testid="name-input" />
              </div>
              <div>
                <label className={labelCls}>Scientific Name</label>
                <input className={inputCls} value={form.scientificName} onChange={e => set("scientificName", e.target.value)} placeholder="Pleurotus ostreatus" data-testid="scientific-name-input" />
              </div>
              <div>
                <label className={labelCls}>Badge</label>
                <select className={inputCls} value={form.badge} onChange={e => set("badge", e.target.value)} data-testid="badge-select">
                  {BADGES.map(b => <option key={b} value={b}>{b || "None"}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Category *</label>
                <select className={inputCls} required value={form.category} onChange={e => set("category", e.target.value)} data-testid="category-select">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Price (Rs.) *</label>
                <input className={inputCls} required type="number" min="1" value={form.price} onChange={e => set("price", e.target.value)} placeholder="199" data-testid="price-input" />
              </div>
              <div>
                <label className={labelCls}>UOM *</label>
                <select className={inputCls} value={form.uom} onChange={e => set("uom", e.target.value)} data-testid="uom-select">
                  {UOMS.map(u => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Stock Qty</label>
                <input className={inputCls} type="number" min="0" value={form.qty} onChange={e => set("qty", e.target.value)} placeholder="50" data-testid="qty-input" />
              </div>
            </div>

            <div>
              <label className={labelCls}>Short Description *</label>
              <textarea className={inputCls} required rows={2} value={form.description} onChange={e => set("description", e.target.value)} placeholder="Brief description shown on cards" data-testid="description-input" />
            </div>

            <div>
              <label className={labelCls}>Short Description (Hindi)</label>
              <textarea className={inputCls} rows={2} value={form.description_hi || ""} onChange={e => set("description_hi", e.target.value)} placeholder="Hindi mein chhota vivaran (optional)" data-testid="description-hi-input" />
            </div>

            {/* Active toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl border border-[#273A2F]" style={{ backgroundColor: '#0F1612' }}>
              <div>
                <p className="text-sm font-medium text-[#F2F4F3]">Visible to Customers</p>
                <p className="text-xs text-[#A8BCAE] mt-0.5">Inactive items are hidden from shop & menu</p>
              </div>
              <button type="button" data-testid="active-toggle"
                onClick={() => set("isActive", !form.isActive)}
                className={`relative w-12 h-6 rounded-full transition-colors ${form.isActive ? "bg-emerald-500" : "bg-[#273A2F]"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.isActive ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>
        )}

        {/* Details Tab */}
        {tab === "details" && (
          <div className="space-y-5 p-6 rounded-2xl border border-[#273A2F]" style={{ backgroundColor: '#16201A' }}>
            <div>
              <label className={labelCls}>Long Description</label>
              <textarea className={inputCls} rows={4} value={form.longDescription} onChange={e => set("longDescription", e.target.value)} placeholder="Detailed description for the product page" data-testid="long-description-input" />
            </div>

            <div>
              <label className={labelCls}>Long Description (Hindi)</label>
              <textarea className={inputCls} rows={4} value={form.long_description_hi || ""} onChange={e => set("long_description_hi", e.target.value)} placeholder="Product page ke liye Hindi mein detail (optional)" data-testid="long-description-hi-input" />
            </div>

            <div>
              <label className={labelCls}>Cooking Tip</label>
              <input className={inputCls} value={form.cookingTip} onChange={e => set("cookingTip", e.target.value)} placeholder="Chef's tip for cooking this mushroom" data-testid="cooking-tip-input" />
            </div>

            {/* Benefits */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + " mb-0"}>Health Benefits</label>
                <button type="button" onClick={() => set("benefits", [...form.benefits, emptyBenefit()])} className="text-xs text-[#D97A46] hover:underline flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {/* eslint-disable-next-line react/no-array-index-key -- fully controlled inputs, safe with index */}
                {form.benefits.map((b, i) => (
                  <div key={`benefit-${i}`} className="flex gap-2">
                    <input className={inputCls} placeholder="Title" value={b.title} onChange={e => { const nb = [...form.benefits]; nb[i] = { ...b, title: e.target.value }; set("benefits", nb); }} />
                    <input className={inputCls} placeholder="Description" value={b.desc} onChange={e => { const nb = [...form.benefits]; nb[i] = { ...b, desc: e.target.value }; set("benefits", nb); }} />
                    <button type="button" onClick={() => set("benefits", form.benefits.filter((_, j) => j !== i))} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg transition-all flex-shrink-0">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Uses */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className={labelCls + " mb-0"}>Uses</label>
                <button type="button" onClick={() => set("uses", [...form.uses, ""])} className="text-xs text-[#D97A46] hover:underline flex items-center gap-1">
                  <Plus className="w-3 h-3" /> Add
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {/* eslint-disable-next-line react/no-array-index-key -- fully controlled inputs, safe with index */}
                {form.uses.map((u, i) => (
                  <div key={`use-${i}`} className="flex gap-1.5">
                    <input className={inputCls} placeholder="e.g. Stir-fry" value={u} onChange={e => { const nu = [...form.uses]; nu[i] = e.target.value; set("uses", nu); }} />
                    <button type="button" onClick={() => set("uses", form.uses.filter((_, j) => j !== i))} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg flex-shrink-0">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Recipes Tab */}
        {tab === "recipes" && (
          <div className="space-y-4">
            {/* eslint-disable-next-line react/no-array-index-key -- fully controlled inputs, safe with index */}
            {form.recipes.map((recipe, ri) => (
              <div key={`recipe-${ri}`} className="p-5 rounded-2xl border border-[#273A2F] space-y-4" style={{ backgroundColor: '#16201A' }}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-[#F2F4F3] text-sm">Recipe {ri + 1}</h3>
                  <button type="button" onClick={() => set("recipes", form.recipes.filter((_, j) => j !== ri))} className="text-red-400 hover:bg-red-400/10 p-1.5 rounded-lg">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><label className={labelCls}>Recipe Name</label><input className={inputCls} value={recipe.name} onChange={e => { const r = [...form.recipes]; r[ri] = { ...recipe, name: e.target.value }; set("recipes", r); }} placeholder="e.g. Mushroom Risotto" /></div>
                  <div className="col-span-2"><label className={labelCls}>Description</label><input className={inputCls} value={recipe.description} onChange={e => { const r = [...form.recipes]; r[ri] = { ...recipe, description: e.target.value }; set("recipes", r); }} placeholder="Brief description" /></div>
                  <div><label className={labelCls}>Time</label><input className={inputCls} value={recipe.time} onChange={e => { const r = [...form.recipes]; r[ri] = { ...recipe, time: e.target.value }; set("recipes", r); }} placeholder="30 mins" /></div>
                  <div><label className={labelCls}>Difficulty</label>
                    <select className={inputCls} value={recipe.difficulty} onChange={e => { const r = [...form.recipes]; r[ri] = { ...recipe, difficulty: e.target.value }; set("recipes", r); }}>
                      {["Easy", "Medium", "Hard"].map(d => <option key={d}>{d}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2"><label className={labelCls}>Recipe Image URL</label><input className={inputCls} value={recipe.image} onChange={e => { const r = [...form.recipes]; r[ri] = { ...recipe, image: e.target.value }; set("recipes", r); }} placeholder="https://..." /></div>
                </div>
                {/* Ingredients */}
                <div>
                  <div className="flex justify-between mb-1.5"><label className={labelCls + " mb-0"}>Ingredients</label>
                    <button type="button" onClick={() => { const r = [...form.recipes]; r[ri] = { ...recipe, ingredients: [...recipe.ingredients, ""] }; set("recipes", r); }} className="text-xs text-[#D97A46]"><Plus className="w-3 h-3 inline" /> Add</button></div>
                  {/* eslint-disable-next-line react/no-array-index-key -- fully controlled inputs */}
                  {recipe.ingredients.map((ing, ii) => (
                    <div key={`ing-${ri}-${ii}`} className="flex gap-1.5 mb-1.5">
                      <input className={inputCls} value={ing} placeholder={`Ingredient ${ii + 1}`} onChange={e => { const r = [...form.recipes]; const ni = [...recipe.ingredients]; ni[ii] = e.target.value; r[ri] = { ...recipe, ingredients: ni }; set("recipes", r); }} />
                      <button type="button" onClick={() => { const r = [...form.recipes]; r[ri] = { ...recipe, ingredients: recipe.ingredients.filter((_, j) => j !== ii) }; set("recipes", r); }} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
                {/* Steps */}
                <div>
                  <div className="flex justify-between mb-1.5"><label className={labelCls + " mb-0"}>Steps</label>
                    <button type="button" onClick={() => { const r = [...form.recipes]; r[ri] = { ...recipe, steps: [...recipe.steps, ""] }; set("recipes", r); }} className="text-xs text-[#D97A46]"><Plus className="w-3 h-3 inline" /> Add</button></div>
                  {/* eslint-disable-next-line react/no-array-index-key -- fully controlled inputs */}
                  {recipe.steps.map((step, si) => (
                    <div key={`step-${ri}-${si}`} className="flex gap-1.5 mb-1.5">
                      <span className="text-xs text-[#A8BCAE] w-5 flex-shrink-0 pt-2.5">{si + 1}.</span>
                      <input className={inputCls} value={step} placeholder={`Step ${si + 1}`} onChange={e => { const r = [...form.recipes]; const ns = [...recipe.steps]; ns[si] = e.target.value; r[ri] = { ...recipe, steps: ns }; set("recipes", r); }} />
                      <button type="button" onClick={() => { const r = [...form.recipes]; r[ri] = { ...recipe, steps: recipe.steps.filter((_, j) => j !== si) }; set("recipes", r); }} className="p-2 text-red-400 hover:bg-red-400/10 rounded-lg flex-shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <button type="button" onClick={() => set("recipes", [...form.recipes, emptyRecipe()])} className="w-full py-3 rounded-xl border border-dashed border-[#273A2F] text-sm text-[#A8BCAE] hover:border-[#D97A46]/30 hover:text-[#D97A46] transition-all flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Add Recipe
            </button>
          </div>
        )}

        {/* Pre-Order Tab */}
        {tab === "preorder" && (
          <div className="p-6 rounded-2xl border border-[#273A2F] space-y-5" style={{ backgroundColor: '#16201A' }}>
            <div className="flex items-center justify-between p-4 rounded-xl border border-[#273A2F]" style={{ backgroundColor: '#0F1612' }}>
              <div>
                <p className="text-sm font-medium text-[#F2F4F3]">Mark as Upcoming / Pre-Order</p>
                <p className="text-xs text-[#A8BCAE] mt-0.5">Customers can pre-order this variety</p>
              </div>
              <button type="button" data-testid="upcoming-toggle"
                onClick={() => set("isUpcoming", !form.isUpcoming)}
                className={`relative w-12 h-6 rounded-full transition-colors ${form.isUpcoming ? "bg-[#D97A46]" : "bg-[#273A2F]"}`}>
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${form.isUpcoming ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>

            {form.isUpcoming && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Pre-Order From *</label>
                  <input className={inputCls} type="date" value={form.preOrderFrom}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e => {
                      set("preOrderFrom", e.target.value);
                      // Clear until if it becomes before the new from date
                      if (form.preOrderUntil && e.target.value > form.preOrderUntil) set("preOrderUntil", "");
                    }}
                    data-testid="preorder-from-input" />
                </div>
                <div>
                  <label className={labelCls}>Pre-Order Until * (max 45 days)</label>
                  <input className={inputCls} type="date" value={form.preOrderUntil}
                    min={form.preOrderFrom || new Date().toISOString().split("T")[0]}
                    max={new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]}
                    onChange={e => set("preOrderUntil", e.target.value)}
                    data-testid="preorder-until-input" />
                  {form.preOrderFrom && !form.preOrderUntil && (
                    <p className="text-xs text-amber-400 mt-1">Select a date after {new Date(form.preOrderFrom).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                  )}
                </div>
                <div className="col-span-2 p-3 rounded-xl border border-amber-500/20 text-xs text-amber-400" style={{ backgroundColor: 'rgba(245,158,11,0.05)' }}>
                  When marked upcoming, customers will see a "Pre-Order" button instead of "Add to Cart". The "Until" date must be after the "From" date.
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-red-400 text-sm">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => navigate("/admin/mushrooms")} className="px-6 py-3 rounded-xl border border-[#273A2F] text-sm text-[#A8BCAE] hover:text-[#F2F4F3] transition-all">
            Cancel
          </button>
          <button type="submit" data-testid="save-mushroom-btn" disabled={saving}
            className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold text-white disabled:opacity-60 transition-all hover:scale-[1.02]"
            style={{ backgroundColor: '#D97A46' }}>
            <Save className="w-4 h-4" /> {saving ? "Saving..." : (isEdit ? "Update Mushroom" : "Create Mushroom")}
          </button>
        </div>
      </form>
    </div>
  );
}
